import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronTop, Pencil } from 'assets/icons';
import PanelCard from 'components/card/PanelCard';
import useModal from 'components/modal/useModal';
import { COMPANY_SUPPLIER_NONE_LABEL } from 'constants/missionSupplierFilters';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';
import UseContext from 'hooks/useContext';
import usefetchdata from 'hooks/usefetchdata';
import ApiDatabase from 'server';
import { EDITOR_ROLES, Field, TWO_COLUMN_GRID } from './overviewLayout';

const SlideOverCompanyOverview = ({
  companyInfo,
  setCompanyInfo,
  // Read beside the company rather than here: mounted only once the company
  // had landed, these two requests started then, and the activity card read
  // "Non renseigné" for as long as they ran.
  pennylaneCategoryList = [],
  bigAccount = '',
  isActivityWaiting = false,
  onReloadPennylaneCategories,
}) => {
  const { userData } = usefetchdata();
  const { objID, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const [isCostEstimateDisplay, setIsCostEstimateDisplay] = useState(false);
  const [
    collectiveInstallationsReferential,
    setCollectiveInstallationsReferential,
  ] = useState([]);
  const [
    activeCollectiveInstallationCodes,
    setActiveCollectiveInstallationCodes,
  ] = useState([]);
  const token = localStorage.getItem('xsrfToken');
  const [role, setRole] = useState(500);

  useEffect(() => {
    if (userData?.role) setRole(userData.role);
  }, [userData]);

  const canEdit = EDITOR_ROLES.includes(role);

  useEffect(() => {
    ApiDatabase.getDisplayCostEstimate(
      { token, idCompany: objID },
      (data) => setIsCostEstimateDisplay(data?.displayCostEstimate),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  const manageDisplayCostEstimate = () => {
    ApiDatabase.putDisplayCostEstimate(
      { token, idCompany: objID, displayCostEstimate: !isCostEstimateDisplay },
      () => {
        setIsCostEstimateDisplay(!isCostEstimateDisplay);
        toast.success(
          'Les estimations de coût sont maintenant visible par le client',
        );
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  };

  useEffect(() => {
    // RG-04: the installations list comes from the back, never hardcoded in the front.
    ApiDatabase.getCollectiveInstallations(
      { token },
      (data) => setCollectiveInstallationsReferential(data.installations ?? []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  useEffect(() => {
    if (!objID) return;
    ApiDatabase.getCollectiveInstallationsCompany(
      { token, idCompany: objID },
      (data) =>
        setActiveCollectiveInstallationCodes(
          (data.installations ?? []).map((installation) => installation.code),
        ),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [objID]);

  useEffect(() => {
    if (modalDataReturn?.description) {
      setCompanyInfo((previous) => ({
        ...previous,
        description: modalDataReturn.description,
      }));
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (modalDataReturn?.type === 'collective_installations_updated') {
      setActiveCollectiveInstallationCodes(
        (modalDataReturn.installations ?? []).map(
          (installation) => installation.code,
        ),
      );
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (
      modalDataReturn?.type === 'company_legal_information_updated' ||
      modalDataReturn?.type === 'company_activity_updated'
    ) {
      setCompanyInfo((previous) => ({
        ...previous,
        ...modalDataReturn.companyInfo,
      }));
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    // Creating a category replaces the activity modal, so it is reopened here
    // once the new category exists — otherwise the edit in progress is lost.
    if (modalDataReturn?.createdCategory) {
      onReloadPennylaneCategories?.();
      setModalDataReturn({});
      setModalData({ idCompany: companyInfo?._id, companyInfo });
      toggle('company_activity');
    }
  }, [modalDataReturn]);

  const openModal = (modalType) => {
    setModalData({ idCompany: companyInfo?._id, companyInfo });
    toggle(modalType);
  };

  const selectedPennylaneCategory = pennylaneCategoryList.find(
    (category) =>
      normalizePennylaneCategoryId(category.id) ===
      normalizePennylaneCategoryId(companyInfo?.pennylane_category_id),
  );

  const activeCollectiveInstallationLabels = collectiveInstallationsReferential
    .filter((installation) =>
      activeCollectiveInstallationCodes.includes(installation.code),
    )
    .map((installation) => installation.label);

  const location = companyInfo?.address
    ? [
        companyInfo.address.city,
        companyInfo.address.department,
        companyInfo.address.street,
      ]
        .filter(Boolean)
        .join(', ')
    : 'Non renseigné';

  // Resolved by the endpoint alongside the company. The card only ever showed
  // one name, and fetching the whole referential to find it cost 766
  // documents — 116 Ko — on every opening.
  const collectiveAgreementLabel =
    companyInfo?.collectiveAgreement?.name ||
    companyInfo?.conv_collective ||
    'Non renseigné';

  return (
    <>
      <PanelCard
        title={'Informations légales'}
        collapsible
        onEdit={canEdit ? () => openModal('company_legal_information') : null}
        contentClassName={TWO_COLUMN_GRID}
      >
          <Field
            label={'Raison sociale'}
            value={companyInfo?.reason_social || 'Non renseigné'}
          />
          <Field
            label={'Nom commercial'}
            value={companyInfo?.name_commercial || 'Non renseigné'}
          />
          <Field
            label={'SIRET'}
            value={companyInfo?.siret || 'Non renseigné'}
          />
          <Field
            label={'Forme juridique'}
            value={companyInfo?.status || 'Non renseigné'}
          />
          <Field
            label={'Fournisseur'}
            value={
              companyInfo?.companySupplier?.name || COMPANY_SUPPLIER_NONE_LABEL
            }
          />
          <Field
            label={'Code APE'}
            value={companyInfo?.code_ape || 'Non renseigné'}
          />
          <Field
            label={'Effectif'}
            value={companyInfo?.staff || 'Non renseigné'}
          />
          <Field label={'Localisation'} value={location} />
          <Field
            label={'Convention collective'}
            value={collectiveAgreementLabel}
          />
      </PanelCard>

      <PanelCard
        title={"Activité de l'entreprise"}
        collapsible
        onEdit={canEdit ? () => openModal('company_activity') : null}
        contentClassName={TWO_COLUMN_GRID}
      >
          {/* A dash while they load: "Non renseigné" would read as a
              settled answer on a field still being fetched. */}
          <Field
            label={'Secteur Pennylane'}
            value={
              isActivityWaiting
                ? '—'
                : selectedPennylaneCategory?.label || 'Non renseigné'
            }
          />
          <Field
            label={'Grand compte'}
            value={
              isActivityWaiting ? '—' : bigAccount?.name || 'Non renseigné'
            }
          />
      </PanelCard>

      <PanelCard
        title={"Brief de l'entreprise"}
        onEdit={() => openModal('edit_description_company')}
        contentClassName={'px-4 py-3 text-gray-500'}
      >
          {companyInfo?.description || 'Aucun brief renseigné'}
      </PanelCard>

      <PanelCard
        title={'Installation collective'}
        onEdit={() => openModal('collective_installations')}
        contentClassName={'px-4 py-3 text-gray-500'}
      >
          {activeCollectiveInstallationLabels.length > 0
            ? activeCollectiveInstallationLabels.join(', ')
            : 'Aucune installation collective'}
      </PanelCard>

      {/* Not part of the mockup, kept because removing it would drop a feature. */}
      <div className={'border border-gray-200 rounded-lg p-3'}>
        <div className={'flex justify-between items-center'}>
          <div className={''}>Estimations de coût</div>
          <input
            type={'checkbox'} className="checkbox-primary"
            checked={isCostEstimateDisplay}
            onChange={() => manageDisplayCostEstimate()}
          />
        </div>
      </div>
    </>
  );
};

export default SlideOverCompanyOverview;
